/**
 * PresetChips - Shared component for filter preset chips
 *
 * Used by both multiselect and daterange filters for consistent:
 * - Toggle behavior (click to select, click again to deselect)
 * - Styling (compact chips matching DataTable design)
 * - Dark mode support
 *
 * This eliminates duplication between filter types.
 */

import { cn } from '../../../lib/utils';
import { FilterPreset } from './types';

interface PresetChipsProps {
  /** Available presets to render */
  presets: FilterPreset[];
  /** Current active values (normalized to array) */
  activeValues: string[];
  /** Called when preset is toggled */
  onToggle: (values: string[], isCurrentlyActive: boolean) => void;
  /** Optional label above chips */
  label?: string;
  /** Additional className for container */
  className?: string;
}

/**
 * Check if a preset exactly matches the current active values
 */
const isPresetActive = (
  presetValues: string[],
  activeValues: string[],
): boolean =>
  presetValues.length === activeValues.length &&
  presetValues.every((v) => activeValues.includes(v));

export const PresetChips = ({
  presets,
  activeValues,
  onToggle,
  label = 'Quick filters:',
  className,
}: PresetChipsProps) => {
  if (!presets.length) return null;

  return (
    <div className={className}>
      {label && (
        <span className="text-xs text-zinc-700 dark:text-zinc-300 mb-1.5 block">
          {label}
        </span>
      )}
      <div className="flex flex-wrap gap-1">
        {presets.map((preset) => {
          const isActive = isPresetActive(preset.values, activeValues);

          return (
            <button
              key={preset.name}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(preset.values, isActive);
              }}
              className={cn(
                'px-2 py-1 text-xs rounded border transition-colors',
                isActive
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100 dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-700',
              )}
            >
              {preset.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};
